import { useCallback, useEffect, useRef, useState } from 'react'
import {
  DEFAULT_MODEL_LIST_PARAMS,
  MODEL_LIST_SESSION_STORAGE_KEY,
} from '../constants'
import { getModelList } from '../services/modelService'
import type { ModelFilterFormValues, ModelListParams, ModelRecord, ModelSortKey } from '../types'

function loadSessionParams(): ModelListParams | null {
  try {
    const saved = sessionStorage.getItem(MODEL_LIST_SESSION_STORAGE_KEY)
    if (!saved) return null
    const parsed = JSON.parse(saved) as Partial<ModelListParams> & { search?: string }
    if (!parsed || typeof parsed !== 'object') return null
    return {
      ...DEFAULT_MODEL_LIST_PARAMS,
      ...parsed,
      page: Number(parsed.page ?? DEFAULT_MODEL_LIST_PARAMS.page),
      perPage: Number(parsed.perPage ?? DEFAULT_MODEL_LIST_PARAMS.perPage),
    }
  } catch {
    return null
  }
}

function saveSessionParams(params: ModelListParams) {
  try {
    sessionStorage.setItem(MODEL_LIST_SESSION_STORAGE_KEY, JSON.stringify(params))
  } catch {
    // ignore session write failures
  }
}

function clearSessionParams() {
  try {
    sessionStorage.removeItem(MODEL_LIST_SESSION_STORAGE_KEY)
  } catch {
    // ignore
  }
}

function toStatusParam(value: string | undefined): ModelListParams['is_active'] {
  return value === '0' || value === '1' ? value : undefined
}

export function modelParamsToFilterForm(params: ModelListParams): ModelFilterFormValues {
  return {
    search: String(params.search ?? ''),
    status: params.is_active === '0' || params.is_active === '1' ? params.is_active : '',
  }
}

export function filterFormToModelParams(
  base: ModelListParams,
  form: ModelFilterFormValues,
): ModelListParams {
  const search = form.search.trim()
  const next: ModelListParams = {
    ...base,
    page: 1,
    search: search || undefined,
    is_active: form.status === '0' || form.status === '1' ? form.status : undefined,
  }
  if (!next.search) delete next.search
  if (next.is_active === undefined) delete next.is_active
  return next
}

export function useModelList() {
  const [params, setParams] = useState<ModelListParams>(
    () => loadSessionParams() ?? DEFAULT_MODEL_LIST_PARAMS,
  )

  const filterSearchValue = params.search || ''
  const [searchDraft, setSearchDraft] = useState({
    source: filterSearchValue,
    value: filterSearchValue,
  })
  const searchInput =
    searchDraft.source === filterSearchValue ? searchDraft.value : filterSearchValue

  const [statusFilter, setStatusFilter] = useState<string>(() =>
    params.is_active === '0' || params.is_active === '1' ? params.is_active : '',
  )

  const [models, setModels] = useState<ModelRecord[]>([])
  const [pagination, setPagination] = useState({ current: 1, last: 1, total: 0 })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const isInitialMount = useRef(true)

  const load = useCallback(async (next: ModelListParams) => {
    setLoading(true)
    setError(null)
    try {
      const response = await getModelList(next)
      setModels(response.data || [])
      setPagination({
        current: response.current ?? 1,
        last: response.last ?? 1,
        total: response.total ?? 0,
      })
    } catch (e) {
      setModels([])
      setPagination({ current: 1, last: 1, total: 0 })
      setError((e as { message?: string }).message || 'Failed to load models')
    } finally {
      setLoading(false)
    }
  }, [])

  const applyFilters = useCallback(
    (overrides?: { search?: string; is_active?: string }) => {
      const targetSearch = overrides?.search !== undefined ? overrides.search : searchInput
      const targetStatus =
        overrides?.is_active !== undefined
          ? overrides.is_active
          : statusFilter === ''
            ? undefined
            : statusFilter

      const trimmedSearch = typeof targetSearch === 'string' ? targetSearch.trim() : undefined
      const next: ModelListParams = {
        ...params,
        page: 1,
        search: trimmedSearch || undefined,
        is_active: toStatusParam(targetStatus),
      }
      if (!next.search) delete next.search
      if (next.is_active === undefined) delete next.is_active
      setParams(next)
      saveSessionParams(next)
      void load(next)
    },
    [params, searchInput, statusFilter, load],
  )

  const setSearchInput = useCallback(
    (val: string) => {
      setSearchDraft({
        source: filterSearchValue,
        value: val,
      })
    },
    [filterSearchValue],
  )

  const handleStatusChange = useCallback(
    (newStatus: string) => {
      setStatusFilter(newStatus)
      applyFilters({ is_active: newStatus })
    },
    [applyFilters],
  )

  // Debounced search — same 500ms pattern as Product list.
  useEffect(() => {
    if (isInitialMount.current) return

    const timer = setTimeout(() => {
      const currentApplied = (params.search || '').trim()
      const draftTrimmed = searchInput.trim()
      if (draftTrimmed !== currentApplied) {
        applyFilters({ search: searchInput })
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [searchInput, params.search, applyFilters])

  const resetFilters = useCallback(() => {
    clearSessionParams()
    const next = { ...DEFAULT_MODEL_LIST_PARAMS }
    setParams(next)
    setSearchDraft({ source: '', value: '' })
    setStatusFilter('')
    void load(next)
  }, [load])

  const changePage = useCallback(
    (page: number) => {
      const next = { ...params, page }
      setParams(next)
      saveSessionParams(next)
      void load(next)
    },
    [params, load],
  )

  const changePerPage = useCallback(
    (perPage: number) => {
      const next = { ...params, perPage, page: 1 }
      setParams(next)
      saveSessionParams(next)
      void load(next)
    },
    [params, load],
  )

  /** Angular model-list `sort()` — new column starts asc; same column toggles. */
  const sort = useCallback(
    (orderBy: ModelSortKey) => {
      const next: ModelListParams = {
        ...params,
        orderBy,
        sortBy:
          params.orderBy === orderBy
            ? params.sortBy === 'asc'
              ? 'desc'
              : 'asc'
            : 'asc',
      }
      setParams(next)
      saveSessionParams(next)
      void load(next)
    },
    [params, load],
  )

  const refetch = useCallback(() => {
    void load(params)
  }, [load, params])

  useEffect(() => {
    void load(params)
    isInitialMount.current = false
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only
  }, [])

  return {
    models,
    params,
    pagination,
    loading,
    error,
    searchInput,
    setSearchInput,
    statusFilter,
    handleStatusChange,
    applyFilters,
    resetFilters,
    changePage,
    changePerPage,
    sort,
    refetch,
  }
}
